// src/utils/report-web-look.js — the report's look, for the client's web page.
// ---------------------------------------------------------------------------
// v0.291.0. Brett, with the client's page beside his PDF: "Why does the client
// report not look the same as mine?"
//
// The PDF is printed in the report's style (Signature, Professional, Bold,
// Compact, Modern, Classic) and its accent colour. The client's web page was
// always DropPin orange on one fixed layout. This reads the settings the PDF
// is built from (the share's own, else the saved report's) and turns them into
// what the web page wears: the cover's paper, type, rules, chip and KPI
// treatment, the section-heading decoration, and the accent.
//
// The accent is whatever the sender picked, so it cannot be trusted to read.
// It is used in three roles, each the nearest shade of the picked colour that
// does its job:
//   accent — the colour itself, on decoration only (strip, band, rule, tile);
//   mark   — reaches 3:1 on the page, for lines, edges and the selected item
//            (WCAG 1.4.11, non-text contrast);
//   ink    — reaches 4.5:1 on every ground it sits on, for words and for
//            button fills under words (WCAG 1.4.3).
// A pale accent on a white page and a dark one on a dark page both stay
// legible, and the colour still reads as theirs. The app's own pages are not
// touched: the page puts these on only while it is showing.

import { DEFAULT_SETTINGS, reportBrand, settingsFromSavedReport } from './reportPdf.js';
import { resolveReportStyle } from './report-styles.js';
import { DEFAULT_BRAND } from './pdf-shell.js';

/**
 * The settings, tier and brand the client's PDF is built with — one rule for
 * the PDF button and the page's look, so the two cannot disagree. The share's
 * own settings win; a share made before those were stored falls back to the
 * saved report's, exactly as ReportScreen opens it.
 */
export function clientReportSettings(pdf) {
  const p = pdf || {};
  const settings = p.settings
    ? { ...DEFAULT_SETTINGS, ...p.settings, filters: { ...DEFAULT_SETTINGS.filters, ...(p.settings.filters || {}) } }
    : settingsFromSavedReport(p.report);
  const tier = p.tier === 'pro' ? 'pro' : 'free';
  const brand = reportBrand({ tier, tenant: tier === 'pro' ? p.tenant : null, settings });
  return { settings, tier, brand };
}

// ---- colour maths (sRGB, WCAG 2.x) ----------------------------------------

const WHITE = [255, 255, 255];
const BLACK = [0, 0, 0];
const NAVY  = [15, 23, 42];     // --text-900 light / --surface-0 dark

/** '#rgb' or '#rrggbb' (the '#' optional) → [r,g,b]; anything else → null. */
export function parseHex(hex) {
  if (typeof hex !== 'string') return null;
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const h = m[1].length === 3 ? m[1].replace(/./g, (c) => c + c) : m[1];
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
}

export function toHex(rgb) {
  return '#' + rgb.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
}

export function luminance(rgb) {
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrast(a, b) {
  const la = luminance(a), lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** a moved t of the way to b, rounded to whole channels. */
export function mix(a, b, t) {
  return a.map((v, i) => Math.round(v + (b[i] - v) * t));
}

// OKLCH (Ottosson's OKLab in polar form). A shade is found by moving only the
// lightness and keeping the hue and chroma, so a deeper orange is still an
// orange; mixing toward black in sRGB would turn it brown.
const toLin = (v) => { const c = v / 255; return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
const fromLin = (c) => 255 * (c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055);

export function toOklch(rgb) {
  const [r, g, b] = rgb.map(toLin);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s;
  return [L, Math.hypot(A, B), Math.atan2(B, A)];
}

function oklchToLinear([L, C, h]) {
  const A = C * Math.cos(h), B = C * Math.sin(h);
  const l = Math.pow(L + 0.3963377774 * A + 0.2158037573 * B, 3);
  const m = Math.pow(L - 0.1055613458 * A - 0.0638541728 * B, 3);
  const s = Math.pow(L - 0.0894841775 * A - 1.2914855480 * B, 3);
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s,
  ];
}

/** OKLCH → sRGB, giving up chroma (never hue) until the colour exists. */
export function fromOklch([L, C, h]) {
  const inGamut = (lin) => lin.every((c) => c >= -1e-4 && c <= 1 + 1e-4);
  let lin = oklchToLinear([L, C, h]);
  if (!inGamut(lin)) {
    let lo = 0, hi = C;
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2;
      if (inGamut(oklchToLinear([L, mid, h]))) lo = mid; else hi = mid;
    }
    lin = oklchToLinear([L, lo, h]);
  }
  return lin.map((c) => Math.max(0, Math.min(255, Math.round(fromLin(Math.max(0, Math.min(1, c)))))));
}

/**
 * The first shade of c, darker ('down') or lighter ('up') in OKLCH lightness
 * steps of 0.005, that reaches `ratio` against every ground. `selfTint`, when
 * given, adds the ground the shade makes of itself (a pill's pale fill under
 * its own words).
 */
export function shadeFor(c, grounds, ratio, direction, selfTint) {
  const [L, C, h] = toOklch(c);
  const end = direction === 'up' ? 1 : 0;
  for (let i = 0; i <= 200; i++) {
    const s = i === 0 ? c : fromOklch([L + (end - L) * (i / 200), C, h]);
    const gs = selfTint ? grounds.concat([selfTint(s)]) : grounds;
    if (gs.every((g) => contrast(s, g) >= ratio)) return s;
  }
  return end ? WHITE : BLACK;
}

function better(on, a, b) { return contrast(on, a) >= contrast(on, b) ? a : b; }

// The grounds each role sits on, light and dark (tokens.css surfaces, the
// Signature cover's bands, and the accent's own tints).
const LIGHT_GROUNDS = [[255, 255, 255], [248, 250, 252], [241, 245, 249], [253, 251, 247]];
const DARK_GROUNDS  = [[15, 23, 42], [11, 18, 32], [30, 41, 59], [45, 45, 45]];
// A Pill is its colour at 13% over the card (white light, navy dark).
const PILL_TINT = 0.87;

// Cover paper per style, light mode (the PDF's first band / theme colour).
const PAPER_LIGHT = {
  signature: { cinematic: null, light: '#fdfbf7', mono: 'var(--surface-0)' },
  professional: '#fdfbf7',
  bold: 'var(--surface-0)',
  compact: 'var(--surface-0)',
  modern: 'var(--surface-1)',
  classic: 'var(--surface-0)',
};
const CINEMATIC_PAPER = 'linear-gradient(180deg, #0f172a 0%, #1e293b 55%, #2d2d2d 100%)';

// The PDF's cover KPI colours: one set for a light cover, one for the dark.
// v0.312.0 — hold = On hold (the PDF's coverKpiColors hold).
const KPI_LIGHT = { total: '#0f172a', open: '#991b1b', hold: '#5b21b6', rect: '#92400e', comp: '#15803d', pct: '#15803d' };
const KPI_DARK  = { total: '#ffffff', open: '#f87171', hold: '#c4b5fd', rect: '#fbbf24', comp: '#86efac', pct: '#fdba74' };

const RADIUS = { signature: 12, professional: 4, bold: 12, compact: 8, modern: 16, classic: 2 };
const TITLE_WEIGHT = { signature: 700, professional: 700, bold: 900, compact: 700, modern: 800, classic: 700 };
const SERIF = 'var(--font-serif)';
const SANS = 'var(--font-ui)';

/**
 * Everything the client page needs to wear the report's look. Pure: the same
 * `pdf` block always gives the same look, and a missing or broken one gives
 * Signature in DropPin orange (what a Free report prints).
 */
export function reportWebLook(pdf) {
  const { settings, tier, brand } = clientReportSettings(pdf);
  const style = resolveReportStyle(settings.styleKey);
  const T = style.tokens || {};
  const C = T.cover || {};
  const H = T.heading || {};
  const key = style.key;
  const themeKey = ['cinematic', 'light', 'mono'].includes(settings.theme) ? settings.theme : 'cinematic';
  const coverKey = C.themeKey || themeKey;
  const cinematic = coverKey === 'cinematic';
  // Theme defaults the cover tokens fall back to (pdf-shell COVER_THEMES).
  const themeStrip = coverKey !== 'mono';
  const themeRule = coverKey === 'mono' ? 1.2 : 0;
  const themeCells = coverKey !== 'mono';

  const accentRgb = parseHex(brand.brandColor) || parseHex(DEFAULT_BRAND.brandColor);
  const accent = toHex(accentRgb);

  const tintL = mix(accentRgb, WHITE, 0.90);
  const tintD = mix(accentRgb, NAVY, 0.84);
  const markL = shadeFor(accentRgb, LIGHT_GROUNDS.slice(0, 2), 3, 'down');
  const markD = shadeFor(accentRgb, DARK_GROUNDS.slice(0, 3), 3, 'up');
  const inkL = shadeFor(accentRgb, LIGHT_GROUNDS.concat([tintL]), 4.5, 'down', (s) => mix(s, WHITE, PILL_TINT));
  const inkD = shadeFor(accentRgb, DARK_GROUNDS.concat([tintD]), 4.5, 'up', (s) => mix(s, NAVY, PILL_TINT));
  // The selected pin sits on a plan (a light drawing) and carries a white
  // number, so it must read under that number as words do.
  const pin = shadeFor(accentRgb, [WHITE], 4.5, 'down');
  const onAccent = better(accentRgb, WHITE, NAVY);
  // A filled label (Modern's pill): the accent itself when white or navy
  // reads on it, else the ink.
  const fillRgb = contrast(accentRgb, onAccent) >= 4.5 ? accentRgb : inkL;
  const onFill = better(fillRgb, WHITE, NAVY);

  const titleSize = C.titleSize || 36;
  const titlePx = Math.max(18, Math.round(titleSize * 0.78));
  const look = {
    styleKey: key,
    coverKey,
    tier,
    brandName: tier === 'pro' ? (brand.brandName || DEFAULT_BRAND.brandName) : DEFAULT_BRAND.brandName,
    logoUrl: tier === 'pro' ? (brand.logoDataUrl || null) : null,
    docLabel: 'Defect report',
    title: settings.title || DEFAULT_SETTINGS.title,
    preparedBy: settings.preparedBy || '',
    cover: {
      cinematic,
      band: Array.isArray(C.bands) && C.bands.some((b) => b && b.color === 'ACCENT'),
      strip: C.accentStrip !== undefined ? !!C.accentStrip : themeStrip,
      ruleH: C.accentRuleH !== undefined ? C.accentRuleH : themeRule,
      ruleFull: !!C.ruleFullWidth,
      labelPill: !!C.labelPill,
      formalRules: !!C.formalRules,
      chipStyle: C.chipStyle || (cinematic ? 'ghost' : 'solid'),
      kpiStyle: (C.kpiStyle || 'cells') === 'cells' && !themeCells ? 'plain' : (C.kpiStyle || 'cells'),
      titleAlign: C.titleAlign === 'center' ? 'center' : 'left',
      titlePx,
      titlePxSmall: Math.max(18, Math.round(titlePx * 0.82)),
      titleWeight: TITLE_WEIGHT[key] || 700,
      font: C.font === 'times' ? SERIF : SANS,
      radius: RADIUS[key] ?? 12,
    },
    heading: {
      deco: ['rule', 'bar', 'underline', 'double', 'plain'].includes(H.deco) ? H.deco : 'rule',
      px: Math.round((H.size || 14) * 0.7 + 6),
      font: H.font === 'times' ? SERIF : SANS,
      caps: !!H.caps,
    },
    colors: {
      accent,
      markLight: toHex(markL), markDark: toHex(markD),
      inkLight: toHex(inkL), inkDark: toHex(inkD),
      tintLight: toHex(tintL), tintDark: toHex(tintD),
      pin: toHex(pin),
      onAccent: toHex(onAccent),
      fill: toHex(fillRgb), onFill: toHex(onFill),
    },
  };

  const shared = {
    '--report-accent': accent,
    '--report-pin': look.colors.pin,
    '--report-on-accent': look.colors.onAccent,
    '--report-fill': look.colors.fill,
    '--report-on-fill': look.colors.onFill,
  };
  const darkPaper = {
    '--report-paper': CINEMATIC_PAPER,
    '--report-paper-text': 'var(--on-chrome)',
    '--report-paper-muted': '#c8c8c8',
    '--report-paper-line': 'rgba(255, 255, 255, 0.16)',
    '--report-paper-border': 'transparent',
    '--report-chip-bg': 'rgba(255, 255, 255, 0.10)',
    '--report-chip-text': 'var(--on-chrome)',
    '--report-kpi-cell': 'rgba(255, 255, 255, 0.06)',
    '--report-kpi-cell-line': 'transparent',
    '--report-kpi-label': '#c8c8c8',
    '--report-cover-ink': look.colors.inkDark,
    '--report-cover-accent': accent,
  };
  const kpiVars = (set) => ({
    '--report-kpi-total': set.total, '--report-kpi-open': set.open, '--report-kpi-hold': set.hold, '--report-kpi-rect': set.rect,
    '--report-kpi-comp': set.comp, '--report-kpi-pct': set.pct,
  });
  const paperLight = typeof PAPER_LIGHT[key] === 'object' ? PAPER_LIGHT[key][coverKey] : PAPER_LIGHT[key];
  const plainPaper = (mode) => ({
    '--report-paper': mode === 'light' ? paperLight : 'var(--surface-0)',
    '--report-paper-text': 'var(--text-900)',
    '--report-paper-muted': 'var(--text-500)',
    '--report-paper-line': 'var(--border-strong)',
    '--report-paper-border': 'var(--border)',
    '--report-chip-bg': 'var(--surface-2)',
    '--report-chip-text': 'var(--text-700)',
    '--report-kpi-cell': key === 'modern' && mode === 'light' ? 'var(--surface-0)' : 'var(--surface-2)',
    '--report-kpi-cell-line': key === 'modern' ? 'var(--border)' : 'transparent',
    // The muted grey reads on the paper but not on a light-grey cell (4.3:1),
    // so a label sitting in one takes the next text step up.
    '--report-kpi-label': mode === 'light' && look.cover.kpiStyle === 'cells' && key !== 'modern' ? 'var(--text-700)' : 'var(--text-500)',
    '--report-cover-ink': mode === 'light' ? look.colors.inkLight : look.colors.inkDark,
    '--report-cover-accent': mode === 'light' ? accent : look.colors.markDark,
  });

  look.vars = {
    light: {
      '--brand-600': look.colors.inkLight,
      '--brand-500': look.colors.inkLight,
      '--brand-700': toHex(mix(inkL, BLACK, 0.15)),
      '--brand-50': look.colors.tintLight,
      '--brand-on': '#ffffff',
      '--report-mark': look.colors.markLight,
      ...shared,
      ...(cinematic ? darkPaper : plainPaper('light')),
      ...kpiVars(cinematic ? KPI_DARK : KPI_LIGHT),
      '--report-title-px': `${look.cover.titlePx}px`,
      '--report-title-px-sm': `${look.cover.titlePxSmall}px`,
    },
    dark: {
      '--brand-600': look.colors.inkDark,
      '--brand-500': look.colors.inkDark,
      '--brand-700': toHex(mix(inkD, WHITE, 0.15)),
      '--brand-50': look.colors.tintDark,
      '--brand-on': toHex(NAVY),
      '--report-mark': look.colors.markDark,
      ...(cinematic ? darkPaper : plainPaper('dark')),
      ...kpiVars(KPI_DARK),
    },
  };
  return look;
}

/** The look as one stylesheet: light on :root, dark on body.dark-mode. */
export function lookCss(look) {
  const block = (sel, vars) => `${sel}{${Object.entries(vars).map(([k, v]) => `${k}:${v};`).join('')}}`;
  return block(':root', look.vars.light) + '\n' + block('body.dark-mode', look.vars.dark);
}
